/**
 * useWebDragDrop
 *
 * Hook that provides HTML5 Drag API handlers for desktop web.
 * Bridges between browser drag events and the existing DragDropContext.
 *
 * Usage in DraggableMealCard:
 *   const { cardRef, isDragActive } = useWebDragDrop({ meal, onMoveMeal });
 *   // Attach cardRef to the outermost web-visible DOM element
 *
 * Usage in MealSlotDropZone:
 *   const { dropZoneProps } = useWebDropZone({ date, mealType, onMoveMeal });
 *   // Spread dropZoneProps onto the drop zone View
 */

import { useRef, useEffect, useCallback } from 'react';
import { PlannedMeal, MealType } from '../redux/mealPlanSlice';
import { useDragDrop, DragOrigin } from '../context/DragDropContext';
import { isDesktopWeb, isMobileWeb } from '../utils/platformDetection';

// Data format stored in dataTransfer
export interface WebDragData {
  mealId: string;
  originDate: string;
  originMealType: string;
}

const DRAG_DATA_TYPE = 'application/x-paprika-meal';

export function serializeDragData(data: WebDragData): string {
  return JSON.stringify(data);
}

export function parseDragData(raw: string): WebDragData | null {
  try {
    const data = JSON.parse(raw);
    if (data.mealId && data.originDate && data.originMealType) {
      return data as WebDragData;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Hook for making a meal card draggable on desktop web.
 *
 * The entire card becomes draggable when hovered — no handle required.
 * Hover card → cursor changes to grab → click and drag anywhere on the card.
 */
export function useWebDrag(options: {
  meal: PlannedMeal;
  enabled?: boolean;
}) {
  const { meal, enabled = true } = options;
  const cardRef = useRef<any>(null);
  const { startDrag, endDrag } = useDragDrop();
  const isActive = enabled && isDesktopWeb();

  useEffect(() => {
    if (!isActive) return;

    const cardEl = cardRef.current as HTMLElement | null;
    if (!cardEl) return;

    let isDragging = false;

    const handleMouseEnter = () => {
      cardEl.setAttribute('draggable', 'true');
      cardEl.style.cursor = 'grab';
    };

    const handleMouseLeave = () => {
      if (isDragging) return;
      cardEl.removeAttribute('draggable');
      cardEl.style.cursor = '';
    };

    const handleDragStart = (e: DragEvent) => {
      if (!e.dataTransfer) return;

      isDragging = true;

      const dragData: WebDragData = {
        mealId: meal.id,
        originDate: meal.scheduledFor,
        originMealType: meal.mealType,
      };

      e.dataTransfer.setData(DRAG_DATA_TYPE, serializeDragData(dragData));
      e.dataTransfer.effectAllowed = 'move';
      cardEl.style.opacity = '0.6';
      cardEl.style.cursor = 'grabbing';

      startDrag(meal, { date: meal.scheduledFor, mealType: meal.mealType });
    };

    const handleDragEnd = () => {
      isDragging = false;
      cardEl.style.opacity = '1';
      cardEl.style.cursor = '';
      cardEl.removeAttribute('draggable');
      endDrag();
    };

    cardEl.addEventListener('mouseenter', handleMouseEnter);
    cardEl.addEventListener('mouseleave', handleMouseLeave);
    cardEl.addEventListener('dragstart', handleDragStart);
    cardEl.addEventListener('dragend', handleDragEnd);

    return () => {
      cardEl.removeEventListener('mouseenter', handleMouseEnter);
      cardEl.removeEventListener('mouseleave', handleMouseLeave);
      cardEl.removeEventListener('dragstart', handleDragStart);
      cardEl.removeEventListener('dragend', handleDragEnd);
      cardEl.removeAttribute('draggable');
      cardEl.style.cursor = '';
    };
  }, [isActive, meal.id, meal.scheduledFor, meal.mealType, startDrag, endDrag]);

  return { cardRef, isActive };
}

/**
 * Hook for making a slot a drop zone on desktop web.
 * Returns event handlers to spread onto a View.
 */
export function useWebDropZone(options: {
  date: string;
  mealType: MealType;
  onMoveMeal?: (mealId: string, newDate: string, newMealType: MealType) => void;
  enabled?: boolean;
}) {
  const { date, mealType, onMoveMeal, enabled = true } = options;
  const dropRef = useRef<any>(null);
  const { updateDropTarget, endDrag, state: dragState } = useDragDrop();
  const isActive = enabled && isDesktopWeb();

  useEffect(() => {
    if (!isActive) return;

    const element = dropRef.current as HTMLElement | null;
    if (!element) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault(); // Required to allow drop
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      updateDropTarget({ date, mealType });
    };

    const handleDragLeave = (e: DragEvent) => {
      // Only clear if actually leaving (not entering a child)
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (relatedTarget && element.contains(relatedTarget)) return;
      updateDropTarget(null);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer) return;

      const raw = e.dataTransfer.getData(DRAG_DATA_TYPE);
      const dragData = parseDragData(raw);

      if (dragData && onMoveMeal) {
        // Only move if target differs from origin
        const isValidDrop =
          dragData.originDate !== date || dragData.originMealType !== mealType;

        if (isValidDrop) {
          onMoveMeal(dragData.mealId, date, mealType);
        }
      }

      endDrag();
    };

    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drop', handleDrop);

    return () => {
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('drop', handleDrop);
    };
  }, [isActive, date, mealType, onMoveMeal, updateDropTarget, endDrag]);

  return { dropRef, isActive };
}

/**
 * Hook for mobile web drag using pointer events.
 * Enables long-press drag on iOS Safari and Chrome mobile.
 */
export function useWebMobileDrag(options: {
  meal: PlannedMeal;
  onMoveMeal?: (mealId: string, newDate: string, newMealType: MealType) => void;
  enabled?: boolean;
}) {
  const { meal, onMoveMeal, enabled = true } = options;
  const cardRef = useRef<any>(null);
  const { startDrag, endDrag, updateDropTarget, findDropZoneAtPoint } = useDragDrop();
  const isActive = enabled && isMobileWeb();

  // Store onMoveMeal in a ref to avoid effect re-runs when callback changes
  const onMoveMealRef = useRef(onMoveMeal);
  onMoveMealRef.current = onMoveMeal;

  // Refs to track drag state across event handlers
  const dragStateRef = useRef({
    isLongPressing: false,
    isDragging: false,
    longPressTimer: null as ReturnType<typeof setTimeout> | null,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    cardRect: null as DOMRect | null,
    lastDropTarget: null as { date: string; mealType: MealType } | null,
    lastDropTargetUpdateTime: 0,
  });

  useEffect(() => {
    if (!isActive) return;

    const cardEl = cardRef.current as HTMLElement | null;
    if (!cardEl) return;

    const LONG_PRESS_DURATION = 350;
    const MOVE_THRESHOLD = 10; // Cancel long press if moved more than this

    const resetDragState = () => {
      const state = dragStateRef.current;
      if (state.longPressTimer) {
        clearTimeout(state.longPressTimer);
        state.longPressTimer = null;
      }

      // Clear any drop zone highlight before resetting state
      if (state.lastDropTarget) {
        const key = `${state.lastDropTarget.date}-${state.lastDropTarget.mealType}`;
        const el = document.querySelector(`[data-dropzone-key="${key}"]`) as HTMLElement;
        if (el) {
          el.style.backgroundColor = '';
          el.style.borderStyle = '';
        }
      }

      state.isLongPressing = false;
      state.isDragging = false;
      state.cardRect = null;
      state.lastDropTarget = null;

      // Reset card styles
      cardEl.style.transform = '';
      cardEl.style.opacity = '';
      cardEl.style.zIndex = '';
      cardEl.style.touchAction = '';

      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };

    const handlePointerDown = (e: PointerEvent) => {
      // Only handle primary pointer (finger or mouse button)
      if (!e.isPrimary) return;

      const state = dragStateRef.current;
      state.startX = e.clientX;
      state.startY = e.clientY;
      state.currentX = e.clientX;
      state.currentY = e.clientY;
      state.isLongPressing = true;
      state.cardRect = cardEl.getBoundingClientRect();

      // CRITICAL: Set touchAction IMMEDIATELY to prevent iOS Safari from
      // interpreting the gesture as a scroll (especially when dragging down)
      cardEl.style.touchAction = 'none';

      // Capture pointer to receive events even if pointer leaves element
      cardEl.setPointerCapture(e.pointerId);

      // Start long press timer
      state.longPressTimer = setTimeout(() => {
        if (!state.isLongPressing) return;

        // Check if pointer moved too much
        const dx = state.currentX - state.startX;
        const dy = state.currentY - state.startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > MOVE_THRESHOLD) {
          // User is scrolling, don't activate drag - restore touchAction
          cardEl.style.touchAction = '';
          resetDragState();
          return;
        }

        // Activate drag mode
        state.isDragging = true;
        cardEl.style.zIndex = '9999';
        cardEl.style.opacity = '0.9';

        // NUCLEAR OPTION: Disable ALL scrolling on the page during drag
        // This prevents iOS Safari from interpreting downward drags as scroll
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        // Notify drag context
        const origin: DragOrigin = {
          date: meal.scheduledFor,
          mealType: meal.mealType,
        };
        startDrag(meal, origin);
      }, LONG_PRESS_DURATION);
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (!e.isPrimary) return;

      const state = dragStateRef.current;
      state.currentX = e.clientX;
      state.currentY = e.clientY;

      // Prevent default during long press AND drag to block scroll on iOS Safari
      if (state.isLongPressing || state.isDragging) {
        e.preventDefault();
      }

      if (state.isDragging) {
        // Calculate offset from start position
        const dx = e.clientX - state.startX;
        const dy = e.clientY - state.startY;

        // Apply CSS transform for visual feedback
        cardEl.style.transform = `translate(${dx}px, ${dy}px) scale(1.05)`;

        // Hit-test drop zones (but DON'T update React state - that causes re-renders which freeze iOS Safari)
        const target = findDropZoneAtPoint(e.clientX, e.clientY);

        // Update visual feedback via direct DOM manipulation (no React re-renders!)
        if (target?.date !== state.lastDropTarget?.date || target?.mealType !== state.lastDropTarget?.mealType) {
          // Remove highlight from previous target
          if (state.lastDropTarget) {
            const prevKey = `${state.lastDropTarget.date}-${state.lastDropTarget.mealType}`;
            const prevEl = document.querySelector(`[data-dropzone-key="${prevKey}"]`) as HTMLElement;
            if (prevEl) {
              prevEl.style.backgroundColor = '';
              prevEl.style.borderStyle = '';
            }
          }

          // Add highlight to new target
          if (target) {
            const newKey = `${target.date}-${target.mealType}`;
            const newEl = document.querySelector(`[data-dropzone-key="${newKey}"]`) as HTMLElement;
            if (newEl) {
              newEl.style.backgroundColor = 'rgba(193, 127, 89, 0.15)';
              newEl.style.borderStyle = 'solid';
            }
          }
        }

        state.lastDropTarget = target;
        // NOTE: We intentionally DON'T call updateDropTarget() during drag to avoid re-renders
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (!e.isPrimary) return;

      const state = dragStateRef.current;

      if (state.isDragging) {
        // Use cached drop target from last pointermove
        const target = state.lastDropTarget;

        if (target && onMoveMealRef.current) {
          const isValidDrop =
            target.date !== meal.scheduledFor || target.mealType !== meal.mealType;

          if (isValidDrop) {
            onMoveMealRef.current(meal.id, target.date, target.mealType);
          }
        }

        endDrag();
      }

      // Release pointer capture
      try {
        cardEl.releasePointerCapture(e.pointerId);
      } catch {}

      resetDragState();
    };

    const handlePointerCancel = (e: PointerEvent) => {
      if (!e.isPrimary) return;

      const state = dragStateRef.current;
      if (state.isDragging) {
        endDrag();
      }

      try {
        cardEl.releasePointerCapture(e.pointerId);
      } catch {}

      resetDragState();
    };

    // Attach pointer event listeners with passive: false to ensure preventDefault works
    const listenerOptions = { passive: false } as AddEventListenerOptions;
    cardEl.addEventListener('pointerdown', handlePointerDown, listenerOptions);
    cardEl.addEventListener('pointermove', handlePointerMove, listenerOptions);
    cardEl.addEventListener('pointerup', handlePointerUp, listenerOptions);
    cardEl.addEventListener('pointercancel', handlePointerCancel, listenerOptions);

    return () => {
      // Only reset drag state if not currently dragging
      // This prevents visual freeze if effect re-runs during drag
      if (!dragStateRef.current.isDragging) {
        resetDragState();
      }
      cardEl.removeEventListener('pointerdown', handlePointerDown);
      cardEl.removeEventListener('pointermove', handlePointerMove);
      cardEl.removeEventListener('pointerup', handlePointerUp);
      cardEl.removeEventListener('pointercancel', handlePointerCancel);
    };
  // Note: onMoveMeal is accessed via ref to prevent effect re-runs when callback changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, meal.id, meal.scheduledFor, meal.mealType, startDrag, endDrag, updateDropTarget, findDropZoneAtPoint]);

  return { cardRef, isActive };
}
